import { useState, type ReactNode } from 'react';
import { useList } from '@hana/data';
import { findCarrier, findPort } from '@hana/domain';
import { useT } from '@hana/i18n';
import { Button, Chip, Drawer, Segmented, StateView, Table, useToast, type Column } from '@hana/ui';
import { IconAlertTriangle, IconBell, IconChevronDown, IconDots, IconEye, IconMapPin, IconFileTypePdf, IconMail, IconRefresh, IconSparkles, IconTable } from '@tabler/icons-react';
import { dm, dmHm } from './format';
import { EVENT_FILTERS, bookingGroup, containerTotals, cutoffs, filterEvents, groupCounts, hasProfile, todos, type BookingGroup, type EventFilter } from './vessel';
import type { Vessel, VesselCall, VesselEvent, VesselFile, VoyageBooking } from './types';
import { forecastOf, type VesselForecast } from './vesselForecast';
import './VesselDrawer.i18n';
import s from './vessel.module.css';

type Tab = 'ov' | 'rot' | 'bk' | 'hist' | 'docs';
const STATUS_TONE: Record<BookingGroup, 'warn' | 'info' | 'ok' | 'neutral'> = { Standby: 'warn', Active: 'info', Released: 'ok', Huỷ: 'neutral' };
const FILE_ICON = { pdf: IconFileTypePdf, mail: IconMail, xls: IconTable } as const;
const KIND_TX: Record<VesselEvent['kind'], string> = { chg: 'vd.kChg', notice: 'Notice', bk: 'Booking', new: 'vd.kNew' };

/** Thẻ gập được của Tổng quan (board: nút "Mở hoặc gập <tiêu đề>", mặc định mở). */
function Card({ title, icon, badge, action, children }: { title: string; icon?: ReactNode; badge?: string; action?: ReactNode; children: ReactNode }) {
  const t = useT();
  const [open, setOpen] = useState(true);
  return (
    <section className={s.card} aria-label={title}>
      <div className={s.cardHead}>
        {icon}
        <h3>{title}</h3>
        {badge && <span className={s.cardBadge}>{badge}</span>}
        {action}
        <button type="button" className={s.fold} aria-expanded={open} aria-label={t('vd.fold', { v: title })} onClick={() => setOpen((o) => !o)}>
          <IconChevronDown size={16} aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : undefined }} />
        </button>
      </div>
      {open && children}
    </section>
  );
}

const dash = (v: string | undefined | null) => v || '—';

function Kv({ rows }: { rows: readonly (readonly [string, string])[] }) {
  return (
    <dl className={s.kv}>
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{dash(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function EditBtn({ label }: { label: string }) {
  const t = useT();
  const toast = useToast();
  return (
    <button type="button" className={s.edit} onClick={() => toast({ msg: t('vd.noWrite') })}>
      {label}
    </button>
  );
}

function Ais({ f, name }: { f: VesselForecast; name: string }) {
  const t = useT();
  return (
    <Card title={t('vd.ais')} icon={<IconMapPin size={18} aria-hidden="true" />} badge={f.nav}>
      <div className={s.map} role="img" aria-label={t('vd.mapAria', { name, sog: f.sog })}>
        <svg aria-hidden="true" width="100%" height="100%" viewBox="0 0 340 170" preserveAspectRatio="none">
          <path d="M0 0 H340 V22 C 300 30, 280 40, 262 46 C 244 52, 238 70, 220 74 C 196 80, 176 70, 150 84 C 120 100, 92 96, 70 120 C 50 140, 30 150, 0 156 Z" fill="var(--ctl)" />
          <path d="M206 118 L 252 56" fill="none" stroke="var(--d-blue)" strokeWidth="2" strokeDasharray="5 5" vectorEffect="non-scaling-stroke" />
        </svg>
        <svg className={s.ship} aria-hidden="true" width="26" height="26" viewBox="193 105 26 26">
          <g transform={`translate(206 118) rotate(${f.heading})`}>
            <path d="M0 -10 L6 7 L0 3 L-6 7 Z" fill="var(--d-amber)" stroke="var(--card)" strokeWidth="1.5" />
          </g>
        </svg>
        <span className={s.mapPort}>{t('vd.caiMep')}</span>
        <span className={s.mapSog}>{name} · {f.sog}</span>
      </div>
      <Kv rows={[[t('vd.pos'), f.pos], [t('vd.speed'), f.sog], [t('vd.status'), f.nav], [t('vd.dist'), f.dist.replace('Cách Cái Mép ', '')], [t('vd.updated'), f.aisAt]]} />
    </Card>
  );
}

function Forecast({ f }: { f: VesselForecast }) {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const c = f.chosen;
  return (
    <Card title={t('vd.fcTitle', { v: f.port })}>
      <div className={s.fcTool}>
        <Button
          variant="secondary"
          size="sm"
          icon={<IconRefresh size={16} aria-hidden="true" />}
          aria-disabled={busy}
          onClick={() => {
            if (busy) return;
            setBusy(true);
            toast({ msg: t('vd.recalcAsked') });
            setTimeout(() => setBusy(false), 4000);
          }}
        >
          {busy ? t('vd.recalcing') : t('vd.recalc')}
        </Button>
      </div>
      <div className={s.fcPick}>
        <div className={s.fcPickHead}>
          <span>{t('vd.sysPick', { v: c.src })}</span>
          <span className={s.sub}>{c.at}</span>
        </div>
        <div className={s.fcPickBody}>
          <div><div className={s.sub}>{t('vd.predEtb')}</div><b>{c.etb}</b></div>
          <div><div className={s.sub}>{t('vd.predEtd')}</div><b>{c.etd}</b><div className={s.sub}>{c.sched}</div></div>
          <Chip tone="warn">{c.delta}</Chip>
        </div>
        <div className={s.conf}>
          <span className={s.sub}>{t('vd.conf')}</span>
          <span className={s.bar} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={c.conf} aria-label={t('vd.conf')}><span style={{ width: `${c.conf}%` }} /></span>
          <b>{c.conf}%</b>
        </div>
      </div>
      <div className={s.sub}>{t('vd.bySrc', { v: f.base })}</div>
      <div className={s.srcList}>
        {f.sources.map((r) => (
          <div key={r.name} className={r.chosen ? `${s.src} ${s.srcOn}` : s.src}>
            <div>
              <div className={s.srcName}>
                <b>{r.name}</b>
                {r.chosen && <Chip tone="info">{t('vd.chosen')}</Chip>}
                {r.status && <Chip tone={r.status === 'OK' ? 'ok' : r.status === 'tạm dừng' ? 'warn' : 'neutral'}>{r.status}</Chip>}
              </div>
              <div className={r.value === '—' ? s.muted : undefined}>{r.value}</div>
              {r.sub && <div className={s.sub}>{r.sub}</div>}
            </div>
            <div className={s.srcR}>
              <b className={r.delta.startsWith('+') ? s.warn : undefined}>{r.delta}</b>
              {r.at && <span className={s.sub}>{t('vd.upd', { v: r.at })}</span>}
              {r.conf > 0 && <span className={s.sub}>{t('vd.confN', { n: r.conf })}</span>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Scans({ f }: { f: VesselForecast }) {
  const t = useT();
  const W = 800, X0 = 40, Y0 = 14, Y1 = 116, MX = 10;
  const pts = f.scans.map((v, i) => [X0 + (i * (W - X0 - 10)) / (f.scans.length - 1), Y1 - (v / MX) * (Y1 - Y0)] as const);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const area = `${line} L${pts[pts.length - 1]![0].toFixed(1)} ${Y1} L${X0} ${Y1} Z`;
  const ticks = [0, 5, 10].map((v) => ({ y: Y1 - (v / MX) * (Y1 - Y0), k: `+${v}g` }));
  return (
    <Card title={t('vd.scans')} badge={t('vd.scansN', { n: f.scans.length })}>
      <div className={s.scanBox} role="img" aria-label={t('vd.scanAria', { n: f.scans.length })}>
        <div className={s.scanTicks}>{ticks.map((k) => <span key={k.k}>{k.k}</span>)}</div>
        <svg aria-hidden="true" width="100%" height="100%" viewBox="40 8 750 114" preserveAspectRatio="none">
          {ticks.map((k) => <line key={k.k} x1="40" x2={W - 10} y1={k.y} y2={k.y} stroke="var(--line)" strokeWidth="1" vectorEffect="non-scaling-stroke" />)}
          <path d={area} fill="var(--pri-bg)" />
          <path d={line} fill="none" stroke="var(--d-blue)" strokeWidth="2.4" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
      </div>
      <div className={s.scanX}><span>{f.scanFrom}</span><span>{f.scanTo}</span></div>
    </Card>
  );
}

function Overview({ v, calls, bookings, go }: { v: Vessel; calls: readonly VesselCall[]; bookings: readonly VoyageBooking[]; go: (k: Tab) => void }) {
  const t = useT();
  const toast = useToast();
  const fc = forecastOf(v);
  const cuts = cutoffs(v);
  const todo = todos(v, bookings);
  const p = v.profile;
  const prev = [...calls].reverse().find((c) => c.state === 'past');
  return (
    <>
      <Card title={t('vd.todo')} icon={<IconAlertTriangle size={18} aria-hidden="true" />}>
        {todo.map((x) => (
          <div key={x.key} className={`${s.todo} ${s[`t_${x.tone}`]}`}>
            <span className={s.dot} aria-hidden="true" />
            <span>{x.text}</span>
            {x.go && (
              <button type="button" className={s.iconBtn} aria-label={t('vd.viewBk')} onClick={() => go(x.go as Tab)}>
                <IconEye size={16} aria-hidden="true" />
              </button>
            )}
            {x.key === 'ntf' && (
              <button type="button" className={s.iconBtn} aria-label={t('vd.notifyCust')} onClick={() => toast({ msg: t('vd.noWrite') })}>
                <IconBell size={16} aria-hidden="true" />
              </button>
            )}
          </div>
        ))}
      </Card>
      {fc && <Ais f={fc} name={v.name} />}
      <Card title={t('vd.voyStatus')}>
        <div className={s.todo}>
          <b>{v.atd ? t('vd.left', { v: `${v.pol} · ${v.terminal}` }) : t('vd.heading', { v: `${v.pol} · ${v.terminal}` })}</b>
          <Chip tone={v.atd ? 'ok' : 'neutral'}>{v.atd ? t('vd.sailed') : t('vd.notSailed')}</Chip>
        </div>
        {prev && <div className={s.sub}>{t('vd.prevPort', { v: prev.name })} · ATD {dmHm(prev.etd)}</div>}
      </Card>
      <Card title={t('vd.cutoffs')}>
        <div className={s.cuts}>
          {cuts.map((c) => (
            <div key={c.key} className={`${s.cut} ${s[`r_${c.remain.cls}`]}`}>
              <span className={s.dot} aria-hidden="true" />
              <span className={s.cutK}>{c.label}</span>
              <b>{dmHm(c.at)}</b>
              <span className={s.remain}>{c.remain.text}</span>
            </div>
          ))}
        </div>
        {v.remark && <p className={s.remark}>{v.remark}</p>}
      </Card>
      <Card title={t('vd.voyage', { v: v.voyage })} action={<EditBtn label={t('vd.edit')} />}>
        <Kv rows={[[t('vd.shipLine'), v.carrier], ['Service', v.service ?? ''], ['Loading terminal', v.terminal], ['POL', v.pol], [t('vd.noticeType'), v.noticeType], [t('vd.changeCount'), String(v.changes)]]} />
      </Card>
      <Card title={t('vd.ident')} action={<EditBtn label={t('vd.editProfile')} />}>
        {p ? (
          <Kv rows={[['IMO', p.imo], ['MMSI', p.mmsi], ['Call sign', p.callSign], [t('vd.flag'), p.flag], [t('vd.shipType'), p.type], [t('vd.lxw'), `${p.length} × ${p.beam} m`], [t('vd.draught'), `${p.draught} m`]]} />
        ) : (
          <p className={s.muted}>{t('vd.noImo')}</p>
        )}
      </Card>
      {fc && <Forecast f={fc} />}
      {fc && <Scans f={fc} />}
    </>
  );
}

function Rotation({ calls, bookings }: { calls: readonly VesselCall[]; bookings: readonly VoyageBooking[] }) {
  const t = useT();
  if (!calls.length) return <StateView state="empty" title={t('vd.noRot')} desc={t('vd.noRotD')} />;
  return (
    <ol className={s.rot} aria-label={t('vd.itinerary')}>
      {calls.map((c) => {
        const n = bookings.filter((b) => b.pod === c.code).length;
        return (
          <li key={c.id} className={`${s.call} ${s[`c_${c.state}`]}`} aria-current={c.state === 'next' ? 'step' : undefined}>
            <span className={s.pin} aria-hidden="true" />
            <div className={s.callMain}>
              <b title={findPort(c.code)?.name ?? c.name}>{c.code}</b>
              <span className={s.muted}>{c.name}</span>
            </div>
            <div className={s.callTime}>
              <span>ETA {dmHm(c.eta)}</span>
              <span>ETD {dmHm(c.etd)}</span>
            </div>
            <span className={s.callSt}>{c.state === 'past' ? t('vd.stLeft') : c.state === 'next' ? t('vd.stNext') : t('vd.stPlan')}</span>
            {n > 0 && (
              <span className={s.callBk}>
                <Chip tone="info">{t('vd.bkN', { n })}</Chip>
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function Bookings({ items }: { items: readonly VoyageBooking[] }) {
  const t = useT();
  const [f, setF] = useState<'all' | BookingGroup>('all');
  const groups = groupCounts(items);
  const shown = items.filter((b) => f === 'all' || bookingGroup(b.status) === f);
  const totals = containerTotals(items);
  const cols: Column<VoyageBooking>[] = [
    { key: 'job', header: 'Job', width: 120, cell: (b) => (<b>{b.job}</b>) },
    { key: 'cus', header: t('vd.hCust'), width: 110, cell: (b) => b.customer },
    { key: 'bk', header: 'Booking', width: 130, minor: true, cell: (b) => dash(b.booking) },
    { key: 'cont', header: 'Cont', width: 80, cell: (b) => b.cont },
    { key: 'ret', header: t('vd.hReturn'), width: 130, minor: true, cell: (b) => dash(b.returnAt) },
    { key: 'pod', header: 'POD · ETA', width: 100, minor: true, cell: (b) => `${b.pod} · ${b.etaPod}` },
    { key: 'st', header: t('vd.status'), width: 130, cell: (b) => <Chip tone={STATUS_TONE[bookingGroup(b.status)]}>{b.status}</Chip> },
    { key: 'ntf', header: t('vd.hLate'), width: 90, minor: true, cell: (b) => (b.notifiedAt ? dm(b.notifiedAt.slice(0, 10)) : b.status === 'RQ Request' ? '–' : <span className={s.warn}>{t('vd.not')}</span>) },
  ];
  if (!items.length) return <StateView state="empty" title={t('vd.noBk')} />;
  return (
    <>
      <div className={s.chips} role="group" aria-label={t('vd.filterBk')}>
        <button type="button" className={s.chipBtn} aria-pressed={f === 'all'} onClick={() => setF('all')}>{t('vd.all')} {items.length}</button>
        {groups.map((g) => (
          <button key={g.group} type="button" className={s.chipBtn} aria-pressed={f === g.group} onClick={() => setF(g.group)}>{g.group} {g.n}</button>
        ))}
      </div>
      <Table label={t('vd.bkOnVoy')} columns={cols} rows={shown} rowKey={(b) => b.id} dense />
      <div className={s.sub}>{Object.entries(totals).map(([k, n]) => `${n}×${k}`).join(' · ')}</div>
    </>
  );
}

function History({ items }: { items: readonly VesselEvent[] }) {
  const t = useT();
  const [f, setF] = useState<EventFilter>('all');
  const shown = filterEvents(items, f);
  return (
    <>
      <Segmented kind="toggle" label={t('vd.filterHist')} value={f} onChange={setF} items={EVENT_FILTERS.map((x) => ({ key: x.key, label: x.label, n: x.key === 'all' ? items.length : items.filter((e) => e.kind === x.key).length }))} />
      {shown.length === 0 ? (
        <StateView state="empty" title={t('vd.noChange')} />
      ) : (
        <ol className={s.hist}>
          {shown.map((e) => (
            <li key={e.id} className={s[`h_${e.kind}`]}>
              <span className={s.pin} aria-hidden="true" />
              <div>
                <div><b>{e.title}</b> <span className={s.muted}>· {t(KIND_TX[e.kind])}</span></div>
                <div>{e.detail}</div>
                <div className={s.sub}>{dmHm(e.at)} · {e.who}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}

function Files({ items }: { items: readonly VesselFile[] }) {
  const t = useT();
  if (!items.length) return <StateView state="empty" title={t('vd.noFiles')} />;
  return (
    <ul className={s.files} aria-label={t('vd.files')}>
      {items.map((f) => {
        const Ic = FILE_ICON[f.type];
        return (
          <li key={f.id}>
            <Ic size={20} aria-hidden="true" />
            <div>
              <b title={f.name}>{f.name}</b>
              <div className={s.sub}>{f.kind} · {f.size} · {f.who} · {dmHm(f.at)}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Body({ v }: { v: Vessel }) {
  const t = useT();
  const where = { vessel: v.id };
  const calls = useList('vesselCall', { where });
  const bks = useList('voyageBooking', { where });
  const evs = useList('vesselEvent', { where });
  const files = useList('vesselFile', { where });
  const [tab, setTab] = useState<Tab>('ov');
  const c = (calls.data?.items ?? []) as VesselCall[];
  const b = (bks.data?.items ?? []) as VoyageBooking[];
  const e = (evs.data?.items ?? []) as VesselEvent[];
  const f = (files.data?.items ?? []) as VesselFile[];
  const pending = calls.isPending || bks.isPending || evs.isPending || files.isPending;
  const toast = useToast();
  return (
    <div className={s.body}>
      <div className={s.actions}>
        <Button variant="ask" size="sm" icon={<IconSparkles size={16} aria-hidden="true" />} onClick={() => toast({ msg: t('vd.noWrite') })}>{t('vd.askNotice')}</Button>
        <Button variant="primary" size="sm" onClick={() => toast({ msg: t('vd.noWrite') })}>{t('vd.sendNotice')}</Button>
        <Button variant="secondary" size="sm" icon={<IconDots size={18} aria-hidden="true" />} aria-label={t('vd.moreActions')} onClick={() => toast({ msg: t('vd.noWrite') })} />
      </div>
      <div className={s.hChips}>
        <Chip tone="info">{findCarrier(v.carrier)?.code ?? v.carrier}</Chip>
        {v.service && <Chip tone="neutral">{v.service}</Chip>}
        <Chip tone={v.atd ? 'ok' : 'warn'}>{v.atd ? t('vd.sailed') : `${t('vd.notSailed')} · ETD ${dm(v.etd.slice(0, 10))}`}</Chip>
        {v.changes > 0 && (
          <button type="button" className={s.chipBtn} onClick={() => setTab('hist')}>
            {t('vd.changedN', { n: v.changes })}
          </button>
        )}
      </div>
      <div className={s.tabs}>
        <Segmented
          label={t('vd.sections')}
          value={tab}
          onChange={setTab}
          items={[{ key: 'ov', label: t('vd.tOv') }, { key: 'rot', label: t('vd.tRot'), n: c.length || undefined }, { key: 'bk', label: 'Booking', n: b.length || undefined }, { key: 'hist', label: t('vd.tHist'), n: e.length || undefined }, { key: 'docs', label: t('vd.tDocs'), n: f.length || undefined }]}
        />
      </div>
      <StateView state={pending ? 'loading' : 'ready'}>
        {tab === 'ov' ? <Overview v={v} calls={c} bookings={b} go={setTab} /> : tab === 'rot' ? <Rotation calls={c} bookings={b} /> : tab === 'bk' ? <Bookings items={b} /> : tab === 'hist' ? <History items={e} /> : <Files items={f} />}
      </StateView>
    </div>
  );
}

/** Hồ sơ tàu dạng ngăn kéo phải (điện thoại: bảng trượt từ đáy). Mở từ danh sách Hồ sơ tàu hoặc tên tàu ở lịch tàu. */
export function VesselDrawer({ vessel, onClose }: { vessel: Vessel | null; onClose: () => void }) {
  const t = useT();
  return (
    <Drawer
      open={!!vessel}
      onOpenChange={(o) => !o && onClose()}
      title={vessel ? `${vessel.name} · ${vessel.voyage}` : ''}
      closeLabel={t('vd.close')}
      readableBackground
      description={vessel ? `${vessel.pol} · ${vessel.terminal} · ETD ${dmHm(vessel.etd)}${hasProfile(vessel) ? ` · IMO ${vessel.profile?.imo}` : ''}` : undefined}
    >
      {vessel && <Body v={vessel} />}
    </Drawer>
  );
}

